import React, { useState, useEffect } from 'react';
import { ExternalLink, Check, Play, X, CreditCard, Mail, Upload, Clock, Key } from 'lucide-react';
import { Button } from '../components/ui/button';

const Applications = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [hasSeenVideo, setHasSeenVideo] = useState(false);

  useEffect(() => {
    // Auto-play video on first visit to the page
    const hasVisited = sessionStorage.getItem('hasVisitedApplications');
    if (!hasVisited) {
      setShowVideoModal(true);
      sessionStorage.setItem('hasVisitedApplications', 'true');
    }
  }, []);

  const handleCloseVideo = () => {
    setShowVideoModal(false);
    setHasSeenVideo(true);
  };

  const handleWatchVideo = () => {
    setShowVideoModal(true);
  };

  return (
    <div className="min-h-screen py-12 bg-[#F9F9F7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16" data-testid="applications-header">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Featured Applications
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Professional tools designed for legal experts, policymakers, and board secretaries
          </p>
          
          {/* Traffic Light Legend */}
          <div className="flex justify-center gap-6 mt-8">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-500"></div>
              <span className="text-sm text-slate-600">Live</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
              <span className="text-sm text-slate-600">Beta</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500"></div>
              <span className="text-sm text-slate-600">Coming Soon</span>
            </div>
          </div>
        </div>

        {/* ==================== LexProcure PH - GREEN (LIVE) ==================== */}
        <section className="mb-20" data-testid="lexprocure-section">
          <div className="relative bg-gradient-to-br from-green-600 to-green-800 rounded-3xl overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <img 
                src="https://customer-assets.emergentagent.com/job_56e2863f-106a-4252-be25-54a4d7e6e68e/artifacts/ohw84nwl_skyline_graybg.png" 
                alt="Background skyline"
                className="w-full h-full object-cover mix-blend-overlay"
              />
            </div>
            <div className="relative z-10 p-8 lg:p-12 text-white">
              <div className="text-center mb-12">
                <div className="inline-block px-4 py-1 bg-green-400 text-green-900 rounded-full text-sm font-semibold mb-6">
                  LIVE
                </div>
                <div className="flex justify-center items-center gap-4 mb-6">
                  <div className="relative p-2 border-2 border-green-400 rounded-lg">
                    <div className="border-2 border-green-400 rounded-lg p-1">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_56e2863f-106a-4252-be25-54a4d7e6e68e/artifacts/p88jm6yj_LexProcurePHLogo.png" 
                        alt="LexProcure PH Logo" 
                        className="h-20 w-auto"
                      />
                    </div>
                  </div>
                  <h2 className="text-4xl sm:text-5xl font-bold">
                    <span className="text-yellow-400">Lex</span>
                    <span className="text-white">Procure PH</span>
                  </h2>
                </div>
                <p className="text-lg text-white/90 max-w-4xl mx-auto leading-relaxed">
                  A digital library designed for everyone who works with Philippine government procurement, from Heads of Procuring Entities, Bids and Awards Committees, their Secretariat and Technical Working Groups to auditors, project managers, and policy makers. It centralizes the New Government Procurement Act (RA 12009), RA 9184, and their Implementing Rules and Regulations into one smart, searchable workspace so users can move from black-letter law to day‑to‑day application in just a few taps.
                </p>
              </div>

              {/* Feature Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="h-16 w-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                    <Check className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Complete Legal Reference</h3>
                  <p className="text-white/90">Access RA 12009, RA 9184, their IRRs, and GPPB Resolution with full metadata</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="h-16 w-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                    <Check className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">AI-Powered Assistant</h3>
                  <p className="text-white/90">Get instant answers to Philippine Procurement Law questions</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="h-16 w-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                    <Check className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Smart Tools & Matrices</h3>
                  <p className="text-white/90">Mode Selection Wizard, comparative matrices, and timeline calculator</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="h-16 w-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                    <Check className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Personal Workspace</h3>
                  <p className="text-white/90">Save bookmarks and notes for quick reference</p>
                </div>
              </div>

              <div className="text-center space-y-4">
                <a href="https://lexprocure-ph.lexdoright.com" target="_blank" rel="noopener noreferrer" data-testid="launch-lexprocure-button">
                  <Button className="bg-white text-green-800 hover:bg-white/90 rounded px-8 py-4 text-lg font-semibold">
                    Launch Application
                    <ExternalLink className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={handleWatchVideo}
                    variant="outline" 
                    className="bg-transparent border-2 border-white text-white hover:bg-white/10 rounded px-6 py-3 text-base font-semibold w-[260px] h-[48px] justify-center"
                  >
                    <Play className="mr-2 h-5 w-5" />
                    Watch Promotional Video
                  </Button>
                  <Button 
                    onClick={() => setShowSubscribeModal(true)}
                    variant="outline" 
                    className="bg-transparent border-2 border-white text-white hover:bg-white/10 rounded px-6 py-3 text-base font-semibold w-[260px] h-[48px] justify-center"
                  >
                    <CreditCard className="mr-2 h-5 w-5" />
                    How to Subscribe
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ==================== LexAgenda - YELLOW (BETA) ==================== */}
        <section className="mb-20" data-testid="lexagenda-section">
          <div className="relative rounded-3xl overflow-hidden">
            {/* Background Image with Teal Tint */}
            <div className="absolute inset-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_legal-hub-36/artifacts/rmeauh50_LexAgenda_backround.png" 
                alt="LexAgenda Background"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-teal-800/85 to-teal-900/90"></div>
            </div>
            
            <div className="relative z-10 p-8 lg:p-12 text-white">
              <div className="text-center mb-12">
                <div className="inline-block px-4 py-1 bg-yellow-400 text-yellow-900 rounded-full text-sm font-semibold mb-6">
                  BETA
                </div>
                <div className="flex justify-center mb-6">
                  <div className="bg-white rounded-xl p-4 shadow-lg">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_lexecom/artifacts/48ysnv4o_LexAgenda-final_logo-transparent.png" 
                      alt="LexAgenda Logo" 
                      className="h-20 w-auto"
                    />
                  </div>
                </div>
                <p className="text-xl text-white/90 mb-2 italic">Corporate Governance Made Simple</p>
                <h3 className="text-2xl font-bold mb-4 text-white">
                  Introducing LexAgenda (Beta): Your PH‑First Board & Stockholders' Meeting Companion
                </h3>
                <p className="text-lg text-white/90 max-w-4xl mx-auto leading-relaxed mb-6">
                  We're excited to invite select Corporate Secretaries, Board Secretariats, and governance professionals to beta test LexAgenda — a PH‑first, multi‑tenant board and stockholders' meeting platform designed around real corporate practice under the Revised Corporation Code.
                </p>
                <p className="text-lg text-white/90 max-w-4xl mx-auto leading-relaxed">
                  LexAgenda helps you manage the full lifecycle of meetings in one secure workspace.
                </p>
              </div>

              {/* Feature Cards - Styled like the reference image */}
              <div className="max-w-4xl mx-auto space-y-4 mb-12">
                <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h4 className="text-lg font-bold mb-2 text-orange-400 flex items-center gap-2">
                    <span className="text-xl">📅</span> Meetings & Governance
                  </h4>
                  <p className="text-white/90">Plan Board and Committee meetings, ASM, and SSM. Use the Governance Portal to share documents for review, conduct polls and voting, and manage collaborative approvals across all stakeholders</p>
                </div>
                
                <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h4 className="text-lg font-bold mb-2 text-orange-400 flex items-center gap-2">
                    <span className="text-xl">📄</span> Document Generation
                  </h4>
                  <p className="text-white/90">Generate notices, agendas, minutes, resolutions, and Secretary's Certificates from reusable templates</p>
                </div>
                
                <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h4 className="text-lg font-bold mb-2 text-orange-400 flex items-center gap-2">
                    <span className="text-xl">📁</span> Digital Records Room
                  </h4>
                  <p className="text-white/90">Keep a secure digital Minutes Book, Resolutions Register, and records room (open, internal, or classified)</p>
                </div>
                
                <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h4 className="text-lg font-bold mb-2 text-orange-400 flex items-center gap-2">
                    <span className="text-xl">✨</span> AI-Assisted Drafting
                  </h4>
                  <p className="text-white/90">Use AI to help draft minutes, resolutions, and certificates under your control</p>
                </div>
              </div>

              {/* Additional Features Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="h-12 w-12 bg-teal-500/30 rounded-xl flex items-center justify-center mb-4">
                    <Check className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-white">Track Action Items</h3>
                  <p className="text-white/80 text-sm">Track action items and implementation of Board and stockholders' decisions</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="h-12 w-12 bg-teal-500/30 rounded-xl flex items-center justify-center mb-4">
                    <Check className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-white">Access Control</h3>
                  <p className="text-white/80 text-sm">Control access to records with internal vs classified visibility, plus a limited public governance library</p>
                </div>
              </div>

              {/* Key Features for Beta Testers */}
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 max-w-4xl mx-auto mb-8 border border-white/30">
                <h4 className="text-xl font-bold mb-4 text-white">Key features for beta testers:</h4>
                <ul className="space-y-3 text-white/90">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span>PH‑aligned templates for notices, agendas, minutes, resolutions, and certificates – with the ability for each company to upload and customize its own formats</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span>Optional AI drafting support (powered by Claude) for minutes, resolutions, and certificates – always under the Corporate Secretary's review and control</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span>Admin dashboard for configuring your Board, Committees, stockholders' meetings, templates, and user roles</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span>Tenant‑based subscription model suitable for OPCs, close corporations, family groups, and multi‑entity holdings</span>
                  </li>
                </ul>
              </div>

              {/* Beta Trial Info */}
              <div className="bg-orange-500/30 rounded-2xl p-6 max-w-4xl mx-auto mb-8 border border-orange-400/50">
                <p className="text-white text-center">
                  <strong>10‑day free trial</strong> on our core packages during the beta phase. Beta feedback will directly shape LexAgenda's roadmap, especially for PH governance and compliance workflows, template customization and SEC‑style defaults, and records classifications.
                </p>
              </div>

              {/* Compliance Note */}
              <p className="text-center text-white/70 text-sm mb-8">
                Aligned with the Revised Corporation Code (RA 11232) and SEC regulations
              </p>

              <div className="text-center space-y-4">
                <a href="https://lexagenda.lexdoright.com/login" target="_blank" rel="noopener noreferrer" data-testid="launch-lexagenda-button">
                  <Button className="bg-orange-500 text-white hover:bg-orange-600 rounded px-8 py-4 text-lg font-semibold">
                    Launch Application
                    <ExternalLink className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                <p className="text-sm text-white/80">
                  If you are a Corporate Secretary, Board Secretariat head, or governance lead and would like to participate in the beta, please post your comments on{' '}
                  <a href="https://lexdoright.com/forum" className="text-orange-400 underline hover:text-orange-300 font-semibold">
                    lexdoright.com/forum
                  </a>{' '}
                  under the topic: <strong>Applications</strong>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ==================== LexDelta - RED BADGE (COMING SOON) - Grayscale ==================== */}
        <section className="mb-20" data-testid="lexdelta-section">
          <div className="relative bg-slate-100 rounded-3xl overflow-hidden border border-slate-200">
            <div className="relative z-10 p-8 lg:p-12">
              <div className="text-center mb-12">
                <div className="inline-block px-4 py-1 bg-red-500 text-white rounded-full text-sm font-semibold mb-6">
                  COMING SOON
                </div>
                <div className="flex justify-center items-center gap-4 mb-6">
                  <div className="h-24 w-24 bg-white rounded-full flex items-center justify-center p-4 shadow-sm">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_56e2863f-106a-4252-be25-54a4d7e6e68e/artifacts/nzy29j4e_LexDelta_logo.png" 
                      alt="LexDelta Logo" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h2 className="text-4xl sm:text-5xl font-bold">
                    <span className="text-slate-800">Lex</span>
                    <span className="text-blue-600">Delta</span>
                  </h2>
                </div>
                <p className="text-lg text-slate-600 max-w-4xl mx-auto leading-relaxed">
                  Guiding you through a clear, step-by-step flow, <span className="font-semibold">Lex</span>Delta helps brainstorm options, refine clause language, and track suggestions until you arrive at a clean, approval-ready version everyone can stand behind.
                </p>
              </div>

              {/* Feature Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
                <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                  <div className="h-12 w-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                    <Check className="h-6 w-6 text-slate-600" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-slate-800">Side-by-side Comparison</h3>
                  <p className="text-slate-600 text-sm">Compare text versions side by side</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                  <div className="h-12 w-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                    <Check className="h-6 w-6 text-slate-600" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-slate-800">Redlined Output</h3>
                  <p className="text-slate-600 text-sm">Generate redlined version output</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                  <div className="h-12 w-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                    <Check className="h-6 w-6 text-slate-600" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-slate-800">Change Log</h3>
                  <p className="text-slate-600 text-sm">Structured change log tracking</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl max-w-5xl w-full overflow-hidden">
            {/* Close Button */}
            <button
              onClick={handleCloseVideo}
              className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white rounded-full p-2 transition-colors"
            >
              <X className="h-6 w-6 text-slate-900" />
            </button>
            
            {/* Video */}
            <div className="aspect-video">
              <video
                controls
                autoPlay
                className="w-full h-full"
                src="https://customer-assets.emergentagent.com/job_lexecom/artifacts/wav4ie17_LexProcurePH.mp4"
              >
                Your browser does not support the video tag.
              </video>
            </div>
            
            {/* Video Info */}
            <div className="p-6 bg-slate-50">
              <h3 className="text-2xl font-bold text-slate-900 mb-2"><span className="text-[#8B1818] font-semibold">Lex</span>Procure PH</h3>
              <p className="text-slate-600">
                Discover how <span className="text-[#8B1818] font-semibold">Lex</span>Procure PH simplifies Philippine government procurement with smart tools and comprehensive legal references.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscribeModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative bg-white rounded-2xl max-w-2xl w-full my-8">
            {/* Close Button */}
            <button
              onClick={() => setShowSubscribeModal(false)}
              className="absolute top-4 right-4 z-10 bg-slate-100 hover:bg-slate-200 rounded-full p-2 transition-colors"
            >
              <X className="h-6 w-6 text-slate-900" />
            </button>
            
            {/* Header */}
            <div className="bg-gradient-to-br from-green-600 to-green-800 p-6 rounded-t-2xl">
              <h2 className="text-2xl font-bold text-white text-center">
                <span className="text-yellow-400">Lex</span>Procure PH Subscription
              </h2>
              <p className="text-white/80 text-center text-sm mt-2">
                While in trial mode, click the blue 'Subscribe Now' button on the right side of the top band of the screen.
              </p>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
              {/* Pricing */}
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-green-600" />
                  I. Pricing
                </h3>
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="font-medium text-slate-700">First Year</span>
                    <span className="font-bold text-green-700">₱699.00 + VAT = ₱782.88</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="font-medium text-slate-700">Succeeding Year</span>
                    <span className="font-bold text-green-700">₱99.00 + VAT = ₱110.88</span>
                  </div>
                </div>
              </div>

              {/* Procedure */}
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-4">II. Procedure</h3>
                <div className="space-y-4">
                  {/* Step 1 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Mail className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">1. Register & Verify</h4>
                      <p className="text-slate-600 text-sm">Create account → Check email for 6-digit code → Enter code on Login</p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CreditCard className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">2. Pay via GCash or Maya</h4>
                      <p className="text-slate-600 text-sm">Subscribe → Select plan → Note the payment details</p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Upload className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">3. Upload Proof of Payment</h4>
                      <p className="text-slate-600 text-sm">Take screenshot of your payment receipt → Upload in the app</p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Clock className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">4. Wait for Admin Approval</h4>
                      <p className="text-slate-600 text-sm">Admin verifies your payment (within 24 hours)</p>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Key className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">5. Activation Completed</h4>
                      <p className="text-slate-600 text-sm">Check your email for confirmation → Subscription Activated</p>
                    </div>
                  </div>

                  {/* Success */}
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200 text-center">
                    <p className="text-green-700 font-semibold">✅ Done! You now have full access.</p>
                  </div>
                </div>
              </div>

              {/* Support */}
              <div className="bg-slate-100 rounded-xl p-4 text-center">
                <p className="text-slate-600">
                  For more queries, you may email{' '}
                  <a href="mailto:support@lexdoright.com?subject=LexProcure%20PH%20Subscription%20Query" className="text-green-600 font-semibold hover:underline">
                    support@lexdoright.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications;
